import {Injectable, NgZone} from '@angular/core';
import {App, URLOpenListenerEvent} from '@capacitor/app';
import {SplashScreen} from '@capacitor/splash-screen';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {createAppVersionRequestCommand} from 'src/app/domain/command/app/app-version.command';
import {AppVersion as AppVersionModel, Platform} from 'src/app/domain/model/app/app-version.model';
import {AuthSignature} from 'src/app/domain/model/auth-signature/auth-signature.model';
import {Auth} from 'src/app/domain/model/auth.model';
import {CustomerPortfolio} from 'src/app/domain/model/customer-portfolio.model';
import {KycStep} from 'src/app/domain/model/kyc-step.model';
import {Kyc} from 'src/app/domain/model/kyc/kyc.model';
import {Lead} from 'src/app/domain/model/lead/lead.model';
import {Onboarding} from 'src/app/domain/model/onboarding.model';
import {User} from 'src/app/domain/model/user.model';
import AppVersionApi from 'src/app/domain/service/api/app-version.api';
import UserApi from 'src/app/domain/service/api/user.api';
import CustomerAreaRouteResolver from 'src/app/domain/service/route-resolver/customer-area-route-resolver.service';
import {Localstorage} from 'src/app/domain/service/storage/localstorage';
import AuthSignatureStore, {AUTHSIGNATURE_NAMESPACE} from 'src/app/domain/store/auth-signature.store';
import AuthStore, {AUTH_NAMESPACE} from 'src/app/domain/store/auth.store';
import CustomerOperativeBlockedStepsStore, {CustomerOperativeBlockedState, OPERATIVE_BLOCKED_NAMESPACE} from 'src/app/domain/store/customer-operative-blocked-steps.store';
import CustomerPortfolioStore, {SELECTED_PORTFOLIO_NAMESPACE} from 'src/app/domain/store/customer-portfolio.store';
import KycStepStore, {KYC_STEP_NAMESPACE} from 'src/app/domain/store/kyc-step.store';
import KycStore, {KYC_NAMESPACE} from 'src/app/domain/store/kyc.store';
import LeadStore, {LEAD_NAMESPACE} from 'src/app/domain/store/lead.store';
import OnboardingStore, {ONBOARDING_NAMESPACE} from 'src/app/domain/store/onboarding.store';
import UserStore, {USER_NAMESPACE} from 'src/app/domain/store/user.store';
import {FacebookPixelTracker} from 'src/app/infrastructure/affiliates/facebook-pixel-tracker';
import {TealiumUtagAnalytics} from 'src/app/infrastructure/analytics/tealium-utag-analytics';
import {GtmService} from 'src/app/infrastructure/analytics/gtm.service';
import {Device} from 'src/app/infrastructure/device/device';
import {InactivityHandler} from 'src/app/infrastructure/device/inactivity-handler';
import {AppEnvironmentService} from 'src/app/infrastructure/environment/app-environment/app-environment.service';
import {Router} from 'src/app/infrastructure/router/router';
import {Modal} from 'src/app/infrastructure/service/modal.service';
import BiometricStatusStore, {BIOMETRIC_NAMESPACE, BiometricStatusState} from 'src/app/infrastructure/store/biometric-status.store';
import {INACTIVITY_NAMESPACE, InactivityStatusState} from 'src/app/infrastructure/store/inactivity-status.store';
import LoginStore from 'src/app/infrastructure/store/login.store';
import {Translator} from 'src/app/infrastructure/translation/translator.service';
import {AppVersionModalPage} from 'src/app/ui/webapp/page/app-version-modal/modal/app-version.modal';
import {EnvironmentModalPage} from 'src/app/ui/webapp/page/environment-modal/modal/environment.modal';
import UtmStore from 'src/app/infrastructure/store/utm.store';
import {MaintenanceStatusService} from 'src/app/infrastructure/service/maintenance-status.service';
import {MaintenanceMonitorService} from 'src/app/infrastructure/service/maintenance-monitor.service';
import MaintenanceRouteResolver from 'src/app/domain/service/route-resolver/maintenance-route-resolver.service';

@Injectable({
  providedIn: 'root'
})
export class Bootstrap {
  public routerEventSubscription: Subscription | undefined;
  private splashHidden = false;

  constructor(
    private storage: Localstorage,
    private authStore: AuthStore,
    private biometricStore: BiometricStatusStore,
    private userStore: UserStore,
    private leadStore: LeadStore,
    private customerPortfolioStore: CustomerPortfolioStore,
    private operationalBlockStore: CustomerOperativeBlockedStepsStore,
    private userApi: UserApi,
    private kycStepStore: KycStepStore,
    private kycStore: KycStore,
    private onboardingStore: OnboardingStore,
    private authSignatureStore: AuthSignatureStore,
    private loginStore: LoginStore,
    private inactivityHandler: InactivityHandler,
    private translator: Translator,
    private router: Router,
    private modalService: Modal,
    private fbTracker: FacebookPixelTracker,
    private tealium: TealiumUtagAnalytics,
    private gtmService: GtmService,
    private cuAreaRouteResolver: CustomerAreaRouteResolver,
    private zone: NgZone,
    private appEnvironment: AppEnvironmentService,
    private device: Device,
    private appVersionApi: AppVersionApi,
    private utmStore: UtmStore,
    private maintenanceStatusService: MaintenanceStatusService,
    private maintenanceMonitorService: MaintenanceMonitorService
  ) {
  }

  public preloadStoredSession(): void {
    try {
      this.initBiometric();
    } catch (e) {
      console.error('preloadStoredSession initBiometric failed:', e);
    }

    const jsonAuth = this.storage.syncGet(AUTH_NAMESPACE);
    const jsonUser = this.storage.syncGet(USER_NAMESPACE);

    if (!jsonAuth || !jsonUser) {
      return;
    }

    try {
      const user: User = JSON.parse(jsonUser);
      this.userStore.saveAuthUser(user);

      const auth: Auth = JSON.parse(jsonAuth);
      this.authStore.saveMe(auth);
    } catch (e) {
      console.error('preloadStoredSession parse failed, clearing corrupt session:', e);
      this.storage.unset(AUTH_NAMESPACE);
      this.storage.unset(USER_NAMESPACE);
    }
  }

  public async bootstrap(): Promise<void> {
    try {
      this.safeRun('translator.setLanguage', () => this.translator.setLanguage('es'));

      // TEMP: health check disabled
      // const isMaintenance = await this.safeRunAsync('checkHealth', () => this.checkHealth(), false);
      // if (isMaintenance) {
      //   return;
      // }
      //
      // this.safeRun('maintenanceMonitor', () => this.maintenanceMonitorService.startMonitoring());

      this.safeRun('initBiometric', () => this.initBiometric());
      this.safeRun('initAuth', () => this.initAuth());
      this.safeRun('initInactivity', () => this.initInactivity());
      this.safeRunAsync('initKycStep', () => this.initKycStep());
      this.safeRunAsync('initKyc', () => this.initKyc());
      this.safeRunAsync('initOnboarding', () => this.initOnboarding());
      this.safeRun('initLead', () => this.initLead());
      this.safeRun('initCustomerPortfolios', () => this.initCustomerPortfolios());
      this.safeRun('initAuthSignature', () => this.initAuthSignature());
      this.safeRun('controlBackButton', () => this.controlBackButton());
      this.safeRun('initDeepLinking', () => this.initDeepLinking());
      this.safeRun('affiliateTrackers', () => this.initializeAffiliateTrackers());
      this.safeRun('analyticsServices', () => this.initializeAnalyticsServices());
      this.safeRun('gtmLoad', () => this.gtmService.loadGTM());
      this.safeRunAsync('initTracking', () => this.initTracking());
      this.safeRun('initOperationalBlock', () => this.initOperationalBlock());

      this.safeRunAsync('checkForAppUpdates', () => this.checkForAppUpdates());

      if (!this.appEnvironment.isProduction() && this.device.isApp()) {
        this.safeRunAsync('openStagingAlertModal', () => this.openStagingAlertModal());
      }
    } finally {
      this.initSplashHiding();
    }
  }

  public async checkForAppUpdates(): Promise<boolean> {

    if (!this.device.isApp()) {
      return true;
    }

    const platform: Platform = this.device.isIosApp() ? 'ios' : 'android';

    try {
      const version = await this.device.appVersion();
      const command = createAppVersionRequestCommand(version, platform);
      const appVersionRemote = await this.appVersionApi.waitFetchAndSync(command);

      if (appVersionRemote?.updateAction && appVersionRemote.updateAction !== 'no_action') {
        await this.appVersionModal(appVersionRemote);
      }
      return true;
    } catch (e) {
      console.error('checkForAppUpdates failed, continuing without version check:', e);
      return true;
    }
  }

  private safeRun(label: string, fn: () => void): void {
    try {
      fn();
    } catch (e) {
      console.error(`Bootstrap step "${label}" failed:`, e);
    }
  }

  private async safeRunAsync<T>(label: string, fn: () => Promise<T>, fallback?: T): Promise<T> {
    try {
      return await fn();
    } catch (e) {
      console.error(`Bootstrap step "${label}" failed:`, e);
      return fallback as T;
    }
  }

  private async checkHealth(): Promise<boolean> {
    const isMaintenance = await this.maintenanceStatusService.checkMaintenanceStatus();

    if (isMaintenance) {
      await this.zone.run(async () => {
        await this.router.navigate([MaintenanceRouteResolver.maintenanceUrl]);
      });
    }

    return isMaintenance;
  }

  private async openStagingAlertModal() {
    await this.modalService.present({
        component: EnvironmentModalPage,
        cssClass: 'modal-size-small'
      }
    );
  }

  private async appVersionModal(appVersion: AppVersionModel) {
    await this.modalService.present({
        component: AppVersionModalPage,
        swipeToClose: false,
        backdropDismiss: false,
        breakpoints: [0.8, 1.0],
        initialBreakpoint: 0.8,
        componentProps: {
          appVersion
        }
      }
    );
  }

  private initializeAffiliateTrackers() {
    this.fbTracker.init();
  }

  private initializeAnalyticsServices() {
    this.tealium.init();
  }

  private initSplashHiding(): void {
    if (this.router.currentUrl().url !== '/' && this.router.currentUrl().url !== '') {
      this.hideSplash();
      return;
    }

    const sub = this.router.changed().pipe(
      filter(route => route.url !== '/' && route.url !== '')
    ).subscribe(() => {
      this.hideSplash();
      sub.unsubscribe();
    });

    setTimeout(() => {
      this.hideSplash();
      sub.unsubscribe();
    }, 4000);
  }

  private hideSplash(): void {
    if (this.splashHidden) {
      return;
    }
    this.splashHidden = true;
    setTimeout(async () => {
      try {
        await SplashScreen.hide({fadeOutDuration: 800});
      } catch (e) {
        console.error('hideSplash failed:', e);
      }
    }, 100);
  }

  private async initTracking() {
    analytics.ready(() => {
      this.routerEventSubscription = this.router.navigationStart().subscribe((route) => {
        const utmData = this.utmStore?.sync();
        const properties: any = {};

        if (utmData) {
          if (utmData.source) {
            properties.utm_source = utmData.source;
            properties.source = utmData.source; // GA4 naming
          }
          if (utmData.medium) {
            properties.utm_medium = utmData.medium;
            properties.medium = utmData.medium; // GA4 naming
          }
          if (utmData.campaign) {
            properties.utm_campaign = utmData.campaign;
            properties.campaign = utmData.campaign; // GA4 naming
          }
          if (utmData.content) {
            properties.utm_content = utmData.content;
            properties.content = utmData.content; // GA4 naming
          }
          if (utmData.term) {
            properties.utm_term = utmData.term;
            properties.term = utmData.term; // GA4 naming
          }
        }

        analytics.page(route.url, properties);

        setTimeout(() => {
          this.gtmService.pushEvent({
            event: 'page_view',
            pagePath: route.url,
            pageTitle: document.title,
            pageLocation: window.location.href,
            ...properties
          });
        }, 100);
      });
    });
  }

  private async initKyc(): Promise<void> {
    const jsonKyc = await this.storage.get(KYC_NAMESPACE);
    if (!jsonKyc) {
      return;
    }

    const kyc: Kyc = JSON.parse(jsonKyc);
    this.kycStore.saveKyc(kyc);
  }

  private async initOnboarding(): Promise<void> {
    const json = await this.storage.get(ONBOARDING_NAMESPACE);
    if (!json) {
      return;
    }

    const onBoarding: Onboarding = JSON.parse(json);
    this.onboardingStore.saveOnboarding(onBoarding);
  }

  private async initKycStep(): Promise<void> {
    const jsonKycStep = await this.storage.get(KYC_STEP_NAMESPACE);
    if (!jsonKycStep) {
      return;
    }

    const kycStep: KycStep = JSON.parse(jsonKycStep);
    this.kycStepStore.saveKycStep(kycStep);
  }

  private initLead() {
    const jsonLead = this.storage.syncGet(LEAD_NAMESPACE);

    if (!jsonLead) {
      return;
    }

    const lead: Lead = JSON.parse(jsonLead);
    this.leadStore.saveLead(lead);
  }

  private initCustomerPortfolios() {
    const jsonSelectedPortfolio = this.storage.syncGet(SELECTED_PORTFOLIO_NAMESPACE);

    if (!jsonSelectedPortfolio) {
      return;
    }

    const portfolio: CustomerPortfolio = JSON.parse(jsonSelectedPortfolio);
    this.customerPortfolioStore.saveSelected(portfolio);
  }

  private initOperationalBlock() {
    const jsonOperationalBlock = this.storage.syncGet(OPERATIVE_BLOCKED_NAMESPACE);

    if (!jsonOperationalBlock) {
      return;
    }

    const operationalBlock: CustomerOperativeBlockedState = JSON.parse(jsonOperationalBlock);
    this.operationalBlockStore.saveAll({...operationalBlock, date: new Date(operationalBlock.date)});
  }

  private initAuth() {
    const jsonAuth = this.storage.syncGet(AUTH_NAMESPACE);
    const jsonUser = this.storage.syncGet(USER_NAMESPACE);
    this.loginStore.initData();

    if (!jsonAuth || !jsonUser) {
      return;
    }

    const user: User = JSON.parse(jsonUser);
    this.userStore.saveAuthUser(user);

    const auth: Auth = JSON.parse(jsonAuth);
    this.authStore.saveMe(auth);

    this.userApi.fetchMe();
  }

  private initAuthSignature() {
    const jsonAuthSignature = this.storage.syncGet(AUTHSIGNATURE_NAMESPACE);

    if (!jsonAuthSignature) {
      return;
    }

    const authSignature: AuthSignature = JSON.parse(jsonAuthSignature);
    this.authSignatureStore.saveAuthSignature(authSignature);
  }

  private initBiometric(): void {
    const jsonBiometric = this.storage.syncGet(BIOMETRIC_NAMESPACE);

    if (!jsonBiometric) {
      return;
    }

    const biometric: BiometricStatusState = JSON.parse(jsonBiometric);
    this.biometricStore.save(biometric.userId, biometric.enabled, biometric.dismissActivationReminder);
  }

  private initInactivity() {
    const json = this.storage.syncGet(INACTIVITY_NAMESPACE);

    App.addListener('appStateChange', async ({isActive}) => {
      this.inactivityHandler.notifyAppStateChange(isActive);

      if (isActive) {
        if (this.splashHidden) {
          this.safeRunAsync('hideSplashOnResume', () => SplashScreen.hide());
        }
        await this.refreshMaintenanceStatus();
      }
    });

    if (!json) {
      return;
    }

    const authSignature: InactivityStatusState = JSON.parse(json);

    this.inactivityHandler.init(new Date(authSignature.lastInteraction));
    this.inactivityHandler.notifyAppStateChange(true);
  }

  private async refreshMaintenanceStatus(): Promise<void> {
    const isMaintenance = await this.maintenanceStatusService.checkMaintenanceStatus();
    const currentUrl = this.router.currentUrl().url;
    const isOnMaintenancePage = currentUrl.includes('/maintenance');

    if (isMaintenance && !isOnMaintenancePage) {
      await this.zone.run(async () => {
        await this.router.navigate([MaintenanceRouteResolver.maintenanceUrl]);
      });
    } else if (!isMaintenance && isOnMaintenancePage) {
      await this.zone.run(async () => {
        await this.router.navigate(['/auth/login']);
      });
    }
  }

  private controlBackButton() {
    App.addListener('backButton', () => {
      this.inactivityHandler.notifyUserInteraction();

      if (this.router.currentUrl().url === this.cuAreaRouteResolver.resolveRoot()) {
        App.exitApp();
        return;
      }

      this.modalService.isOpen()
        ? this.modalService.dismiss(undefined)
        : this.router.navigateToBack();
    });
  }

  private initDeepLinking() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        let slug = event.url.split('app.finizens.com').pop();
        slug = slug.replace('/#/', '/');
        slug = slug.replace('/_/', '/');

        if (slug) {
          this.router.navigateByUrl(slug);
        }
      });
    });
  }
}
